import type { NavigationGuardWithThis, RouteRecordRaw } from 'vue-router'
import { useModuleVisibility, type ReportePredefinido } from '@/composables/useModuleVisibility'
import { useCompanyStore } from '@/stores/company'
import type { PredefinedReport } from './types'

// Mismo patrón que shipment-requests: la compañía debe tener showAiAgent: true.
// En un hard reload el watch del store aún no terminó de traer la compañía,
// así que se espera la carga antes de decidir.
const requireAiAgent: NavigationGuardWithThis<undefined> = async (_to, _from, next) => {
  const companyStore = useCompanyStore()
  if (!companyStore.currentCompanyData && companyStore.selectedCompanyId) {
    await companyStore.loadCompanyData(companyStore.selectedCompanyId)
  }
  companyStore.currentCompanyData?.showAiAgent === true ? next() : next({ name: 'shipments' })
}

// Acceso directo por cliente: la misma regla que el menú (flag de la compañía
// más la lista de uids autorizados, en useModuleVisibility) — independiente de
// showAiAgent, para poder mostrar el reporte sin el módulo de Asistente AI
// visible. La API del backend no depende de esto: es visibilidad de la app.
// En un hard reload la compañía aún no está en el store, así que se espera.
const requireReporte = (report: ReportePredefinido): NavigationGuardWithThis<undefined> => async (_to, _from, next) => {
  const companyStore = useCompanyStore()
  if (!companyStore.currentCompanyData && companyStore.selectedCompanyId) {
    await companyStore.loadCompanyData(companyStore.selectedCompanyId)
  }
  useModuleVisibility().isReportVisible(report) ? next() : next({ name: 'shipments' })
}

// Vista directa del último reporte guardado de ese tipo (se genera y guarda si
// la compañía aún no tiene ninguno), sin navegación de vuelta a "Mis dashboards".
// El slug es también el id del módulo del menú y el segmento de la ruta.
const predefinedReportRoute = (report: PredefinedReport): RouteRecordRaw => ({
  path: report.slug,
  name: `${report.slug}-view`,
  component: () => import('./pages/SavedReportViewPage.vue'),
  meta: { predefinedReport: report },
  beforeEnter: requireReporte(report.slug),
})

export const assistantRoutes: RouteRecordRaw[] = [
  predefinedReportRoute({ slug: 'control-tower', type: 'control_tower', label: 'Torre de Control' }),
  predefinedReportRoute({ slug: 'order-audit', type: 'order_audit', label: 'Auditoría de Órdenes' }),
  predefinedReportRoute({ slug: 'container-control', type: 'container_control', label: 'Control de Contenedores' }),
  {
    path: 'assistant',
    name: 'assistant-home',
    component: () => import('./pages/AssistantPage.vue'),
    beforeEnter: requireAiAgent,
  },
  {
    path: 'assistant/dashboards',
    name: 'assistant-dashboards',
    component: () => import('./pages/SavedDashboardsPage.vue'),
    beforeEnter: requireAiAgent,
  },
  {
    path: 'assistant/dashboards/:id',
    name: 'assistant-dashboard-view',
    component: () => import('./pages/SavedDashboardViewPage.vue'),
    beforeEnter: requireAiAgent,
  },
  {
    path: 'assistant/reports/:id',
    name: 'assistant-report-view',
    component: () => import('./pages/SavedReportViewPage.vue'),
    beforeEnter: requireAiAgent,
  },
]

// Sin DashboardLayout (sin sidebar ni header): el router las monta en la raíz.
// Mismos guards que la vista guardada: sesión (requiresAuth) y showAiAgent.
export const assistantStandaloneRoutes: RouteRecordRaw[] = [
  {
    path: '/dashboard/assistant/dashboards/:id/pantalla',
    name: 'assistant-dashboard-screen',
    component: () => import('./pages/DashboardScreenPage.vue'),
    meta: { requiresAuth: true },
    beforeEnter: requireAiAgent,
  },
]
